import { defineStore, acceptHMRUpdate } from 'pinia'
import { ref, computed } from 'vue'
import { useAuth0 } from '@auth0/auth0-vue'
import { useQueryClient } from '@tanstack/vue-query'
import { log } from 'src/services/app/log.service'
import { localStorageService } from 'src/services/app/storage.service'
import { accountService } from 'src/services/api/account.service'
import { fcmService } from 'src/services/api/fcm.service'
import { usePostHog } from 'src/composables/app/ui/usePostHog'
import { auth0LogoutRedirectUri, auth0RedirectUri, auth0Audience } from 'src/config'
import {
  ANALYTICS_EVENTS,
  API_ERRORS,
  AUTH_FLOW_APP,
  AUTH_FLOW_REASONS,
  STORAGE_KEYS,
  ERROR_TYPES,
} from 'src/services/consts/api.const'
import { BROADCAST_TOPICS } from 'src/services/consts/app.const'
import { broadcastService } from 'src/services/app/broadcast.service'
import { resetStores } from 'stores/reset.store'
import { ROUTES } from 'src/router/const'
import { isValidRedirectRoute } from 'src/services/helpers/security.helper'
import {
  ID_TOKEN_EXPIRY_BUFFER_SECONDS,
  getIdTokenDetails,
  getIdTokenExp,
  getTokenDetailsFromClaims,
  isIdTokenUsable,
} from 'src/services/helpers/auth.helper'

const AUTH_FLOW_TEXT_MAX_LENGTH = 200
const TOKEN_EVENT_KEYS_MAX_SIZE = 20

export const useAuthStore = defineStore('auth', () => {
  const {
    // Auth0 state
    user: auth0User,
    idTokenClaims,
    isLoading,

    // Auth0 methods
    logout: auth0Logout,
    loginWithRedirect: auth0Login,
    getAccessTokenSilently: auth0GetToken,
  } = useAuth0()
  const { reset, captureEvent, captureException } = usePostHog()
  const queryClient = useQueryClient()

  // AppState
  const isLoggingOut = ref(false)
  const isLoggingIn = ref(false)
  const isRedirectingToAuthRouter = ref(false)
  const redirectRoute = ref(localStorageService.getCachedData(STORAGE_KEYS.REDIRECT_ROUTE) || null)
  const capturedTokenEventKeys = new Set()

  // Getters
  const idToken = computed(() => idTokenClaims.value?.__raw)
  const userSub = computed(() => auth0User.value?.sub)
  const userName = computed(() => {
    const firstName = auth0User.value?.given_name || ''
    const lastName = auth0User.value?.family_name || ''
    const fallbackName = auth0User.value?.name || ''
    const fullName = [firstName, lastName].filter(Boolean).join(' ')
    return firstName ? `${fullName}` : fallbackName || 'Member'
  })

  /**
   * Logout user via Auth0 /v2/logout (kills shared SSO session)
   * @param {Object} options - Logout options
   * @param {boolean} options.fromOtherTab - Whether logout was triggered from another tab
   * @param {boolean} options.authError - Whether logout is due to authentication error (skips backend logout)
   * @param {string} options.event - Activity title. Menu logout uses "Signed out"
   * @param {Object} [options.details] - Extra event properties (request path, error code, token timing)
   */
  const logout = async ({
    fromOtherTab = false,
    authError = false,
    event = ANALYTICS_EVENTS.SIGNED_OUT,
    details,
  } = {}) => {
    try {
      isLoggingOut.value = true
      if (!(authError || fromOtherTab)) {
        // Logout doesnt throw error if fails
        await accountService.logout()
      }

      if (!fromOtherTab) {
        broadcastService.post(BROADCAST_TOPICS.LOGOUT)
        captureAuthFlow(event, details)
      }
      broadcastService.closeChannel()

      reset()

      accountService.clearAuth0LoginCache()
      fcmService.clearTokenCache()

      resetStores()
      queryClient.clear()

      // Real logout: SDK navigates to Auth0 /v2/logout -> kills the shared SSO
      // session for ALL apps -> lands on the router's logged-out screen.
      await auth0Logout({
        logoutParams: {
          returnTo: _buildAuthRouterUrl(AUTH_FLOW_REASONS[event]),
        },
      })
    } catch (err) {
      // Only reached when logout failed (success navigates away) - unstick the UI
      isLoggingOut.value = false
      captureException(err, {
        error_type: ERROR_TYPES.AUTH,
        error_name: `[${API_ERRORS.LOGOUT_AUTH_ERROR}] ${err.message}`,
        original_error_message: err.message,
        error_context: 'Auth: logout',
        from_other_tab: fromOtherTab,
        auth_error: authError,
      })
      throw err
    }
  }

  /**
   * Send the user to the auth router's sign-in screen (no shared SSO session).
   * @param {Object} [options]
   * @param {string} [options.reason] - Auth0 error code, e.g. 'login_required'
   * @param {string} [options.description] - Auth0 error_description
   * @param {string} [options.page] - Route name the user was trying to open
   */
  const redirectToAuthRouterLogin = ({ reason, description, page } = {}) => {
    isRedirectingToAuthRouter.value = true
    captureAuthFlow(ANALYTICS_EVENTS.SENT_TO_SIGN_IN, { reason, description, page })
    window.location.href = _buildAuthRouterUrl(reason)
  }

  const login = async () => {
    if (isLoggingIn.value || isRedirectingToAuthRouter.value) {
      return
    }
    isLoggingIn.value = true
    try {
      await auth0Login({
        appState: { target: redirectRoute.value },
        authorizationParams: {
          redirect_uri: auth0RedirectUri,
          audience: auth0Audience,
          scope: 'openid profile email',
          prompt: 'none', // key: attempt SSO only, no UI; returns error=login_required fast if no SSO
        },
      })
    } catch (err) {
      isLoggingIn.value = false
      log.error('Login error:', err)
      captureException(err, {
        error_type: ERROR_TYPES.AUTH,
        error_name: `[${API_ERRORS.LOGIN_AUTH_ERROR}] ${err.message}`,
        original_error_message: err.message,
        error_context: 'Auth: login',
      })
      throw err
    }
  }

  /**
   * Set redirect route with validation to prevent open redirect vulnerabilities
   * @param {string|null} route - Route to redirect to after authentication
   */
  const setRedirectRoute = (route = null) => {
    // Clear redirect if null
    if (!route) {
      redirectRoute.value = null
      localStorageService.remove(STORAGE_KEYS.REDIRECT_ROUTE)
      return
    }

    // Skip if trying to redirect to default app route
    if (route === '/' + ROUTES.APP) return

    // Validate route (defense against open redirect)
    if (!isValidRedirectRoute(route)) {
      return // Validation failed, logged by helper
    }

    // Route is valid - store it
    localStorageService.cacheData(STORAGE_KEYS.REDIRECT_ROUTE, route, 1000 * 60 * 5) // 5 minutes
    redirectRoute.value = route
  }

  const getAuthData = () => {
    return {
      idToken,
      userSub,
    }
  }

  /**
   * Acquire an id_token for API Bearer auth.
   * Auth0 only auto-refreshes on access-token expiry; we force a silent refresh
   * (cacheMode: 'off') when the id_token itself is expired or near expiry.
   * Throws when the session can't be refreshed so callers treat it as auth-expired;
   * the thrown error carries `authReason` ('refresh_failed' | 'token_not_usable').
   */
  const getIdToken = async () => {
    let cached
    try {
      cached = await auth0GetToken({ detailedResponse: true })
    } catch (err) {
      captureTokenEventOnce({
        event: ANALYTICS_EVENTS.REFRESH_FAILED,
        cachedIdToken: undefined,
        details: { forced: false, ..._getAuth0ErrorDetails(err) },
      })
      throw _withAuthReason(err, 'refresh_failed')
    }
    if (cached?.id_token && isIdTokenUsable(cached.id_token)) {
      return cached.id_token
    }

    const cachedIdToken = cached?.id_token
    const tokenExpiresInBefore = getIdTokenDetails(cachedIdToken).token_expires_in_s
    let fresh
    try {
      fresh = await auth0GetToken({
        detailedResponse: true,
        cacheMode: 'off',
      })
    } catch (err) {
      captureTokenEventOnce({
        event: ANALYTICS_EVENTS.REFRESH_FAILED,
        cachedIdToken,
        details: {
          forced: true,
          ..._getAuth0ErrorDetails(err),
          token_expires_in_s_before: tokenExpiresInBefore,
        },
      })
      throw _withAuthReason(err, 'refresh_failed')
    }

    if (!fresh?.id_token || !isIdTokenUsable(fresh.id_token)) {
      captureTokenEventOnce({
        event: ANALYTICS_EVENTS.REFRESH_TOKEN_NOT_USABLE,
        cachedIdToken,
        details: {
          buffer_s: ID_TOKEN_EXPIRY_BUFFER_SECONDS,
          ...getIdTokenDetails(fresh?.id_token),
          has_id_token: fresh?.id_token ? undefined : false,
        },
      })
      throw _withAuthReason(
        new Error('Session expired - unable to refresh id_token'),
        'token_not_usable'
      )
    }

    const freshDetails = getIdTokenDetails(fresh.id_token)
    captureTokenEventOnce({
      event: ANALYTICS_EVENTS.REFRESHED_SESSION_EARLY,
      cachedIdToken,
      details: {
        buffer_s: ID_TOKEN_EXPIRY_BUFFER_SECONDS,
        token_expires_in_s_before: tokenExpiresInBefore,
        token_expires_in_s_after: freshDetails.token_expires_in_s,
        id_token_lifetime_s: freshDetails.id_token_lifetime_s,
      },
    })
    return fresh.id_token
  }

  /**
   * Capture an auth-flow event with the ids shared across router, aigov and aimgmt
   * PostHog projects. Never pass tokens, emails, account ids, bodies, or full URLs.
   * @param {string} event - Sentence-style event title from ANALYTICS_EVENTS
   * @param {Object} [details] - Extra properties; null/undefined values are dropped
   */
  function captureAuthFlow(event, details = {}) {
    try {
      captureEvent(
        event,
        _toAuthFlowProperties({
          app: AUTH_FLOW_APP,
          auth0_user_id: auth0User.value?.sub,
          ...getTokenDetailsFromClaims(idTokenClaims.value || {}),
          ...details,
        })
      )
    } catch (err) {
      log.error('Auth flow event failed', err)
    }
  }

  // Parallel requests all hit getIdToken at once; one row per event per cached token.
  function captureTokenEventOnce({ event, cachedIdToken, details }) {
    const eventKey = `${event}:${getIdTokenExp(cachedIdToken) ?? 'none'}`
    if (capturedTokenEventKeys.has(eventKey)) return
    if (capturedTokenEventKeys.size >= TOKEN_EVENT_KEYS_MAX_SIZE) capturedTokenEventKeys.clear()
    capturedTokenEventKeys.add(eventKey)
    captureAuthFlow(event, details)
  }

  return {
    auth0User,
    redirectRoute,
    userName,
    isLoading,
    isLoggingOut,
    isRedirectingToAuthRouter,

    login,
    logout,
    redirectToAuthRouterLogin,
    setRedirectRoute,
    getAuthData,
    getIdToken,
    captureAuthFlow,
  }
})

function _buildAuthRouterUrl(reason) {
  const params = new URLSearchParams({ loggedOut: 'true', from: AUTH_FLOW_APP })
  if (reason) params.set('reason', reason)
  return `${auth0LogoutRedirectUri}?${params}`
}

function _getAuth0ErrorDetails(err) {
  return {
    code: err?.error || err?.name,
    description: err?.error_description || err?.message,
  }
}

function _withAuthReason(err, authReason) {
  if (err && typeof err === 'object') err.authReason = authReason
  return err
}

function _toAuthFlowProperties(properties) {
  return Object.fromEntries(
    Object.entries(properties)
      .filter(([, value]) => value != null)
      .map(([key, value]) => [
        key,
        typeof value === 'string' ? value.slice(0, AUTH_FLOW_TEXT_MAX_LENGTH) : value,
      ])
  )
}

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot))
}
